<template>
  <div class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
    <div class="max-w-md w-full">
      <div class="bg-white rounded-lg shadow-lg p-8">
        <div class="text-center mb-8">
          <h1 class="text-3xl font-bold text-gray-800">Şifre Sıfırla</h1>
          <p class="text-gray-600 mt-2">Sıfırlama bağlantısı almak için e-postanızı girin</p>
        </div>

        <!-- Success Message -->
        <div v-if="successMessage" class="mb-6 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
          <div class="flex items-center">
            <i class="fas fa-check-circle mr-2"></i>
            <span>{{ successMessage }}</span>
          </div>
        </div>

        <!-- Forgot Password Form -->
        <form v-if="!successMessage" @submit.prevent="handleForgotPassword" class="space-y-6">
          <div>
            <label for="email" class="block text-sm font-medium text-gray-700 mb-2">
              E-posta Adresi
            </label>
            <input
              id="email"
              type="email"
              v-model="email"
              required
              class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="E-posta adresinizi girin"
            >
          </div>

          <button
            type="submit"
            :disabled="loading"
            class="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <span v-if="loading" class="flex items-center justify-center">
              <i class="fas fa-spinner fa-spin mr-2"></i>
              Sıfırlama bağlantısı gönderiliyor...
            </span>
            <span v-else class="flex items-center justify-center">
              <i class="fas fa-envelope mr-2"></i>
              Sıfırlama Bağlantısı Gönder
            </span>
          </button>
        </form>

        <!-- Error Message -->
        <div v-if="error" class="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div class="flex items-center">
            <i class="fas fa-exclamation-circle mr-2"></i>
            <span>{{ error }}</span>
          </div>
        </div>

        <!-- Back to Login -->
        <div class="mt-6 text-center">
          <router-link
            to="/login"
            class="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center justify-center"
          >
            <i class="fas fa-arrow-left mr-2"></i>
            Girişe Dön
          </router-link>
        </div>

        <!-- Try Again -->
        <div v-if="successMessage" class="mt-6 text-center">
          <button
            @click="resetForm"
            class="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Başka bir sıfırlama bağlantısı gönder
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { apiService } from '../services/apiService'
import type { ForgotPasswordRequest, ApiError } from '../types/api'

const router = useRouter()

const email = ref<string>('')
const loading = ref<boolean>(false)
const error = ref<string | null>(null)
const successMessage = ref<string | null>(null)

const handleForgotPassword = async (): Promise<void> => {
  try {
    loading.value = true
    error.value = null

    const requestData: ForgotPasswordRequest = {
      email: email.value
    }

    const response = await apiService.forgotPassword(requestData)
    
    successMessage.value = response.message
    console.log('Password reset email sent successfully')
  } catch (err) {
    const apiError = err as ApiError
    error.value = apiError.error || 'Failed to send reset email. Please try again.'
    console.error('Forgot password error:', err)
  } finally {
    loading.value = false
  }
}

const resetForm = (): void => {
  email.value = ''
  error.value = null
  successMessage.value = null
}

onMounted(() => {
  // Clear any existing error states when component mounts
  error.value = null
  successMessage.value = null
})
</script>

<style scoped>
/* Additional styles if needed */
</style> 